import {
  CodeBlockModel,
  ListBlockModel,
  NoteBlockModel,
  NoteDisplayMode,
  ParagraphBlockModel,
  type RootBlockModel,
} from '@blocksuite/affine-model';
import { focusTextModel, type RichText } from '@blocksuite/affine-rich-text';
import { matchModels } from '@blocksuite/affine-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Store } from '@blocksuite/store';
import { effect } from '@preact/signals-core';
import { css, html } from 'lit';
import { property, query, state } from 'lit/decorators.js';

const DOC_BLOCK_CHILD_PADDING = 24;

export class DocTitle extends WithDisposable(ShadowlessElement) {
  private _isUpdatingTitle = false;
  private _updateMetaTitleHandler?: () => void;
  private readonly _updateTitleDebounceTimer?: number;

  static override styles = css`
    .doc-title-container {
      box-sizing: border-box;
      font-family: var(--affine-font-family);
      font-size: var(--affine-font-base);
      line-height: var(--affine-line-height);
      color: var(--affine-text-primary-color);
      font-size: 40px;
      line-height: 50px;
      font-weight: 700;
      outline: none;
      resize: none;
      border: 0;
      width: 100%;
      max-width: var(--affine-editor-width);
      margin-left: auto;
      margin-right: auto;
      padding: 38px 0;

      padding-left: var(
        --affine-editor-side-padding,
        ${DOC_BLOCK_CHILD_PADDING}px
      );
      padding-right: var(
        --affine-editor-side-padding,
        ${DOC_BLOCK_CHILD_PADDING}px
      );
    }

    /* Extra small devices (phones, 640px and down) */
    @container viewport (width <= 640px) {
      .doc-title-container {
        padding-left: ${DOC_BLOCK_CHILD_PADDING}px;
        padding-right: ${DOC_BLOCK_CHILD_PADDING}px;
      }
    }

    .doc-title-container-empty::before {
      content: 'Title';
      color: var(--affine-placeholder-color);
      position: absolute;
      opacity: 0.5;
      pointer-events: none;
    }

    .doc-title-container:disabled {
      background-color: transparent;
    }
  `;

  private _getOrCreateFirstPageVisibleNote() {
    const note = this._rootModel?.children.find(
      (child): child is NoteBlockModel =>
        matchModels(child, [NoteBlockModel]) &&
        child.props.displayMode !== NoteDisplayMode.EdgelessOnly
    );
    if (note) return note;

    const noteId = this.doc.addBlock('affine:note', {}, this._rootModel, 0);
    return this.doc.getBlock(noteId)?.model as NoteBlockModel;
  }

  private readonly _onTitleKeyDown = (event: KeyboardEvent) => {
    if (event.isComposing || this.doc.readonly) return;
    if (!this._std) return;

    if (event.key === 'Enter') {
      this._std.event.active = true;
      event.preventDefault();
      event.stopPropagation();

      const inlineRange = this.inlineEditor?.getInlineRange();
      if (inlineRange) {
        const rightText = this._rootModel?.props.title.split(inlineRange.index);
        const newFirstParagraphId = this.doc.addBlock(
          'affine:paragraph',
          { text: rightText },
          this._getOrCreateFirstPageVisibleNote(),
          0
        );
        if (this._std) focusTextModel(this._std, newFirstParagraphId);
      }
    } else if (event.key === 'ArrowDown') {
      this._std.event.active = true;
      event.preventDefault();
      event.stopPropagation();

      const note = this._getOrCreateFirstPageVisibleNote();
      const firstText = note?.children.find(block =>
        matchModels(block, [
          ParagraphBlockModel,
          ListBlockModel,
          CodeBlockModel,
        ])
      );
      if (firstText) {
        if (this._std) focusTextModel(this._std, firstText.id);
      } else {
        const newFirstParagraphId = this.doc.addBlock(
          'affine:paragraph',
          {},
          note,
          0
        );
        if (this._std) focusTextModel(this._std, newFirstParagraphId);
      }
    } else if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  private readonly _updateTitleInMeta = async () => {
    if (!this._rootModel || this._isUpdatingTitle) return;

    this._isUpdatingTitle = true;

    try {
      // 先将当前内容进行首尾空格裁剪并同步到模型中
      const rawTitle = this._rootModel.props.title.toString();
      const currentTitle = rawTitle.trim();

      if (rawTitle !== currentTitle) {
        const updateMetaTitle = this._updateMetaTitleHandler;
        if (updateMetaTitle) {
          this._rootModel.props.title.yText.unobserve(updateMetaTitle);
        }

        this._rootModel.props.title.yText.doc?.transact(() => {
          this._rootModel?.props.title.delete(
            0,
            this._rootModel.props.title.length
          );
          this._rootModel?.props.title.insert(currentTitle, 0);
        });

        if (updateMetaTitle) {
          this._rootModel.props.title.yText.observe(updateMetaTitle);
        }

        this.requestUpdate();
      }

      // 类型断言，因为我们知道这是 EnhancedWorkspaceMetaImpl
      const meta = this.doc.workspace.meta as any;
      const finalTitle = await meta.handleTitleUpdate(this.doc.id, {
        title: currentTitle,
      });

      // 如果返回的title与当前title不同，需要更新_rootModel中的title
      if (finalTitle !== currentTitle) {
        // 先移除observer避免死循环
        const updateMetaTitle = this._updateMetaTitleHandler;
        if (updateMetaTitle) {
          this._rootModel.props.title.yText.unobserve(updateMetaTitle);
        }

        // 使用批量更新避免UI闪烁
        this._rootModel.props.title.yText.doc?.transact(() => {
          // 一次性替换整个内容，避免先删除再插入造成的闪烁
          this._rootModel?.props.title.delete(
            0,
            this._rootModel.props.title.length
          );
          this._rootModel?.props.title.insert(finalTitle, 0);
        });

        // 重新添加observer
        if (updateMetaTitle) {
          this._rootModel.props.title.yText.observe(updateMetaTitle);
        }

        // 强制触发UI更新
        this.requestUpdate();
      }
    } finally {
      this._isUpdatingTitle = false;
    }
  };

  private readonly _onTitleBlur = () => {
    if (this.doc.readonly || this._isUpdatingTitle) return;

    // 在失去焦点时检查并更新标题唯一性
    this._updateTitleInMeta().catch(e => {
      console.error('Error updating title in meta:', e);
    });
  };

  private get _std() {
    return this._viewport?.querySelector('editor-host')?.std;
  }

  private get _rootModel() {
    return this.doc.root as RootBlockModel | null;
  }

  private get _viewport() {
    return (
      this.closest<HTMLElement>('.affine-page-viewport') ??
      this.closest<HTMLElement>('.affine-edgeless-viewport')
    );
  }

  get inlineEditor() {
    return this._richTextElement.inlineEditor;
  }

  get inlineEditorContainer() {
    return this._richTextElement.inlineEditorContainer;
  }

  override connectedCallback() {
    super.connectedCallback();

    this._isReadonly = this.doc.readonly;
    this._disposables.add(
      effect(() => {
        if (this._isReadonly !== this.doc.readonly) {
          this._isReadonly = this.doc.readonly;
          this.requestUpdate();
        }
      })
    );

    this._disposables.addFromEvent(this, 'keydown', this._onTitleKeyDown);

    // 添加blur事件监听器
    this._disposables.addFromEvent(this, 'blur', this._onTitleBlur, true);

    // Workaround for inline editor skips composition event
    this._disposables.addFromEvent(
      this,
      'compositionstart',
      () => (this._isComposing = true)
    );

    this._disposables.addFromEvent(
      this,
      'compositionend',
      () => (this._isComposing = false)
    );

    const updateMetaTitle = () => {
      let newTitleString = this._rootModel?.props.title.toString() || '';
      let newTitleArray = newTitleString.split('/');
      if (newTitleArray.length > 1) {
        const originalTitleString = newTitleString;
        newTitleString = newTitleArray.join('');
        const selection = window.getSelection();
        let cursorPosition = 0;
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const originalOffset = range.startOffset;

          // 计算新文本中对应的光标位置
          // 需要计算在原文本中，从开始到光标位置之间有多少个 '/' 字符
          let slashCountBeforeCursor = 0;
          for (
            let i = 0;
            i < originalOffset && i < originalTitleString.length;
            i++
          ) {
            if (originalTitleString[i] === '/') {
              slashCountBeforeCursor++;
            }
          }
          // 新位置 = 原位置 - 光标前的 '/' 数量
          cursorPosition = Math.max(0, originalOffset - slashCountBeforeCursor);
        }
        this._rootModel?.props.title.yText.doc?.transact(() => {
          this._rootModel?.props.title.delete(
            0,
            this._rootModel.props.title.length
          );
          this._rootModel?.props.title.insert(newTitleString, 0);
        });
        // 使用 requestAnimationFrame 确保在下一帧重置
        requestAnimationFrame(() => {
          const newSelection = window.getSelection();
          if (newSelection && newSelection.rangeCount > 0) {
            try {
              const newRange = document.createRange();
              const range = newSelection.getRangeAt(0);
              const textNode = range.startContainer;

              // 确保 textNode 是文本节点
              if (textNode.nodeType !== Node.TEXT_NODE) {
                // 如果不是文本节点，尝试找到文本节点
                const walker = document.createTreeWalker(
                  textNode,
                  NodeFilter.SHOW_TEXT,
                  null
                );
                const firstTextNode = walker.nextNode();
                if (!firstTextNode) {
                  return; // 如果找不到文本节点，直接返回
                }
                // 确保光标位置不超过文本节点的长度
                const maxOffset = firstTextNode.textContent?.length || 0;
                const safePosition = Math.min(cursorPosition, maxOffset);
                newRange.setStart(firstTextNode, safePosition);
                newRange.setEnd(firstTextNode, safePosition);
              } else {
                // 确保光标位置不超过文本节点的长度
                const maxOffset = textNode.textContent?.length || 0;
                const safePosition = Math.min(cursorPosition, maxOffset);
                newRange.setStart(textNode, safePosition);
                newRange.setEnd(textNode, safePosition);
              }

              newSelection.removeAllRanges();
              newSelection.addRange(newRange);
            } catch (error) {
              // 如果设置光标位置失败，静默处理，避免中断用户操作
              console.warn('Failed to restore cursor position:', error);
            }
          }
        });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('title-invalid'));
        }
      }
      this.doc.workspace.meta.setDocMeta(this.doc.id, {
        title: newTitleString,
      });
      this.requestUpdate();
    };

    // 保存handler引用以便在_updateTitleInMeta中使用
    this._updateMetaTitleHandler = updateMetaTitle;

    if (this._rootModel) {
      const rootModel = this._rootModel;
      rootModel.props.title.yText.observe(updateMetaTitle);
      this._disposables.add(() => {
        rootModel.props.title.yText.unobserve(updateMetaTitle);
      });
    }
  }

  override render() {
    const isEmpty = !this._rootModel?.props.title.length && !this._isComposing;

    return html`
      <div
        class="doc-title-container ${isEmpty
          ? 'doc-title-container-empty'
          : ''}"
        data-block-is-title="true"
      >
        <rich-text
          .yText=${this._rootModel?.props.title.yText}
          .undoManager=${this.doc.history.undoManager}
          .verticalScrollContainerGetter=${() => this._viewport}
          .readonly=${this.doc.readonly}
          .enableFormat=${false}
          .wrapText=${this.wrapText}
        ></rich-text>
      </div>
    `;
  }

  @state()
  private accessor _isComposing = false;

  @state()
  private accessor _isReadonly = false;

  @query('rich-text')
  private accessor _richTextElement!: RichText;

  @property({ attribute: false })
  accessor doc!: Store;

  @property({ attribute: false })
  accessor wrapText = true;
}
